import React, { useCallback } from "react"
import type { MenuItemType } from "@mallardbay/lib-react-components"
import { useDisclosure } from "@mallardbay/lib-react-components"
import { faUsers } from "@fortawesome/free-solid-svg-icons/faUsers"
import { faUserXmark } from "@fortawesome/free-solid-svg-icons/faUserXmark"
import { faRotate } from "@fortawesome/free-solid-svg-icons/faRotate"
import { faArrowRightFromBracket } from "@fortawesome/free-solid-svg-icons/faArrowRightFromBracket"
import { faArrowsLeftRight } from "@fortawesome/pro-regular-svg-icons/faArrowsLeftRight"
import { faCreditCard } from "@fortawesome/free-solid-svg-icons/faCreditCard"
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear"
import { faStripeS } from "@fortawesome/free-brands-svg-icons/faStripeS"
import { navigate } from "gatsby"

import { PROFILE_MENU_LABELS } from "~config/constants"
import { useUser } from "~contexts/user-provider"
import { useAuth } from "~utils/hooks/use-auth"
import useStopImpersonation from "~utils/hooks/use-stop-impersonation"
import { usePortalEntitiesContext } from "~contexts/outfitter-provider"
import { usePersistedPortalEntities } from "~utils/hooks/use-persisted-portal-entities"
import { useStripeAccountInfo } from "~utils/hooks/use-stripe-account-info"
import { getCleanEnum } from "~utils/helpers/string-helpers"
import { PortalType } from "~graphql/generated/types"
import COPY from "~config/copy-constants"
import {
    getRequestSubscriptionChangesUrl,
    navigateWithHardReload,
    getAccountPageUrl,
    openInNewTab,
    ROUTES,
} from "~utils/helpers/navigation-helpers"
import ImpersonationModal from "~components/shared/auth/impersonation-modal"

const STRIPE_DASHBOARD_LABEL = "Stripe dashboard"

export default function useProfileMenuConfig() {
    const { isAdmin, isImpersonating } = useUser()
    const { portalType, portalTypes, setPortalType } =
        usePortalEntitiesContext()
    const { clearAllPersistedPortalEntities } = usePersistedPortalEntities()
    const handleLogout = useHandleLogout({ isImpersonating })
    const { stripeAccountInfo } = useStripeAccountInfo()
    const portalMenuItemConfig = getPortalMenuItemConfig({
        portalType,
        portalTypes,
    })

    const { stopImpersonation: handleStopViewing } = useStopImpersonation({
        onSuccess: () => {
            clearAllPersistedPortalEntities()
            navigateWithHardReload()
        },
    })

    const {
        isOpen: isImpersonationOpen,
        onOpen: onImpersonationOpen,
        onClose: onImpersonationClose,
    } = useDisclosure()

    const menuItems = buildMenuItems({
        isAdmin,
        isImpersonating,
        handleStopViewing: () => handleStopViewing(),
        onImpersonationOpen,
        portalMenuItemConfig,
        showStripeAccount: Boolean(stripeAccountInfo?.is_onboarded),
        stripeLoginLink: stripeAccountInfo?.login_link ?? "",
        handleLogout,
        setPortalType,
    })

    const impersonationModal = (
        <ImpersonationModal
            isOpen={isImpersonationOpen}
            onClose={onImpersonationClose}
        />
    )

    return { menuItems, impersonationModal }
}

function buildMenuItems({
    isAdmin,
    isImpersonating,
    handleStopViewing,
    onImpersonationOpen,
    portalMenuItemConfig,
    showStripeAccount,
    stripeLoginLink,
    handleLogout,
    setPortalType,
}: BuildMenuItemsParams): MenuItemType[] {
    const items: MenuItemType[] = []

    if (isImpersonating) {
        items.push({
            label: PROFILE_MENU_LABELS.STOP_VIEWING_AS_LABEL,
            icon: faUserXmark,
            onClick: handleStopViewing,
        })
    }

    if (isAdmin && !isImpersonating) {
        items.push({
            label: PROFILE_MENU_LABELS.VIEW_AS_LABEL,
            icon: faUsers,
            onClick: onImpersonationOpen,
        })
    }

    if (portalMenuItemConfig.portalType) {
        items.push({
            label: portalMenuItemConfig.label,
            icon: faArrowsLeftRight,
            onClick: () => {
                setPortalType(portalMenuItemConfig.portalType)
                navigate(portalMenuItemConfig.route)
            },
        })
    }

    addDividerToLastItem(items)

    if (showStripeAccount) {
        items.push({
            label: STRIPE_DASHBOARD_LABEL,
            icon: faStripeS,
            onClick: () => {
                openInNewTab(stripeLoginLink)
            },
        })
    }

    items.push({
        label: PROFILE_MENU_LABELS.ACCOUNT_SETTINGS,
        icon: faGear,
        href: getAccountPageUrl(),
    })

    items.push({
        label: PROFILE_MENU_LABELS.BILLING,
        icon: faCreditCard,
        href: ROUTES.BILLING,
    })

    items.push({
        label: PROFILE_MENU_LABELS.REQUEST_SUBSCRIPTION_CHANGES,
        icon: faRotate,
        onClick: () => {
            window.location.href = getRequestSubscriptionChangesUrl()
        },
        hasDivider: true,
    })

    items.push({
        label: PROFILE_MENU_LABELS.LOG_OUT_LABEL,
        icon: faArrowRightFromBracket,
        onClick: handleLogout,
    })

    return items
}

function addDividerToLastItem(items: MenuItemType[]) {
    const lastItem = items[items.length - 1]

    if (!lastItem) return

    lastItem.hasDivider = true
}

function useHandleLogout({ isImpersonating }: { isImpersonating?: boolean }) {
    const { stopImpersonation } = useStopImpersonation()
    const { logout } = useAuth()

    return useCallback(async () => {
        if (isImpersonating) await stopImpersonation()
        await logout()
    }, [isImpersonating, stopImpersonation, logout])
}

function getPortalMenuItemConfig({
    portalType,
    portalTypes,
}: {
    portalType: PortalType | null | undefined
    portalTypes: PortalType[]
}) {
    const portalSwitchTo = portalTypes.find((type) => type !== portalType)
    const label = `${COPY.SWITCH_TO} ${getCleanEnum(portalSwitchTo)}`
    const isRegionalDirector = portalSwitchTo === PortalType.RegionalDirector
    const route = isRegionalDirector ? ROUTES.SOURCE_TRIPS : ROUTES.HOME

    return { portalType: portalSwitchTo, label, route }
}

interface BuildMenuItemsParams {
    isAdmin?: boolean
    isImpersonating?: boolean
    handleStopViewing: () => void
    onImpersonationOpen: () => void
    portalMenuItemConfig: ReturnType<typeof getPortalMenuItemConfig>
    showStripeAccount: boolean
    stripeLoginLink: string
    handleLogout: () => Promise<void>
    setPortalType: (type: PortalType | undefined) => void
}
